import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useImpersonation } from './useImpersonation';

export type AppRole = 'admin' | 'department_manager' | 'procurement_manager' | 'provider_manager' | 'qa_manager' | 'ma_manager' | 'tech_apps' | 'tech_infrastructure' | 'creative_manager';
export type DepartmentType = 'aso' | 'product' | 'compliance' | 'ad_monetization' | 'user_acquisition';

export interface UserRoleData {
  role: AppRole | null;
  department: DepartmentType | null;
  loading: boolean;
  error: string | null;
}

export interface UserRolePermissions extends UserRoleData {
  isAdmin: boolean;
  isProcurementManager: boolean;
  isProviderManager: boolean;
  isDepartmentManager: boolean;
  isQAManager: boolean;
  isMAManager: boolean;
  isTechApps: boolean;
  isTechInfrastructure: boolean;
  isCreativeManager: boolean;
  canAccessLab: (labType: string) => boolean;
  canCreateApps: boolean;
  canDeleteApps: boolean;
  canInviteUsers: boolean;
  canManageRoles: boolean;
  canViewAllTasks: boolean;
  allowedLabs: string[];
  allowedPages: string[];
}

const LAB_MAPPING: Record<DepartmentType, string[]> = {
  aso: ['aso', 'store-optimization'],
  product: ['product', 'usability'],
  compliance: ['compliance'],
  ad_monetization: ['ad-monetization', 'ad_monetization'],
  user_acquisition: ['user-acquisition', 'user_acquisition']
};

export function useUserRole(): UserRolePermissions {
  const { user } = useAuth();
  const { activeSession, isImpersonating, loading: impersonationLoading } = useImpersonation();
  const [roleData, setRoleData] = useState<UserRoleData>({
    role: null,
    department: null,
    loading: true,
    error: null
  });

  // Use refs to access latest values without triggering re-renders
  const activeSessionRef = useRef(activeSession);
  const fetchingRef = useRef(false);
  const lastFailAtRef = useRef<number | null>(null);
  const subscribedRef = useRef(false);

  useEffect(() => {
    activeSessionRef.current = activeSession;
  }, [activeSession]);

  useEffect(() => {
    const traceId = `UR-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,6)}`;
    console.log('[useUserRole] useEffect triggered', { 
      traceId,
      userId: user?.id, 
      isImpersonating, 
      impersonationLoading
    });
    
    const fetchUserRole = async () => {
      const t0 = performance.now();
      console.log('[useUserRole] fetchUserRole:start', { traceId, userId: user?.id });

      if (fetchingRef.current) {
        console.log('[useUserRole] fetchUserRole:skip (already fetching)', { traceId });
        return;
      }
      if (lastFailAtRef.current && Date.now() - (lastFailAtRef.current ?? 0) < 30000) {
        console.warn('[useUserRole] Backing off after failure (30s)', { traceId });
        setRoleData(prev => ({ ...prev, loading: false }));
        return;
      }
      fetchingRef.current = true;

      if (!user) {
        setRoleData({
          role: null,
          department: null,
          loading: false,
          error: null,
        });
        console.log('[useUserRole] fetchUserRole:skip (no user)', { traceId });
        fetchingRef.current = false;
        return;
      }

      // Check localStorage cache first to avoid DB on every render
      const cacheKey = `user_role_${user.id}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached && !isImpersonating) {
        try {
          const parsed = JSON.parse(cached);
          setRoleData({
            role: parsed.role,
            department: parsed.department,
            loading: false,
            error: null,
          });
          console.log('[useUserRole] Using cached role', { traceId, cached: parsed });
          fetchingRef.current = false;
          return;
        } catch (e) {
          localStorage.removeItem(cacheKey);
        }
      }

      // Do not block on impersonation state; proceed and we'll reconcile once it resolves
      if (impersonationLoading) {
        setRoleData(prev => ({ ...prev, loading: true }));
        console.log('[useUserRole] impersonationLoading=true, continuing', { traceId });
      }

      try {
        // If impersonating, use impersonation data
        if (isImpersonating && activeSessionRef.current) {
          const session = activeSessionRef.current;
          
          // Clear any cached role data when impersonating
          if (user?.id) {
            localStorage.removeItem(`user_role_${user.id}`);
          }
          
          if (session.impersonated_role) {
            console.log('[useUserRole] Impersonating role', { traceId, role: session.impersonated_role, department: session.impersonated_department });
            setRoleData({
              role: session.impersonated_role as any,
              department: session.impersonated_department as any,
              loading: false,
              error: null,
            });
            return;
          } else if (session.impersonated_user_id) {
            console.log('[useUserRole] Impersonating user, fetching role for:', { traceId, userId: session.impersonated_user_id });
            const [ { data: roleRes, error: roleErr }, { data: deptRes, error: deptErr } ] = await Promise.all([
              supabase.rpc('get_user_role', { _user_id: session.impersonated_user_id }),
              supabase.rpc('get_user_department', { _user_id: session.impersonated_user_id })
            ]);

            const roleVal = (roleRes as string) || null;
            const deptVal = (deptRes as string) || null;

            console.log('[useUserRole] Impersonated user role fetched successfully', { 
              traceId, 
              role: roleVal, 
              department: deptVal,
              impersonatedUserId: session.impersonated_user_id 
            });

            setRoleData({
              role: roleVal as any,
              department: deptVal as any,
              loading: false,
              error: (roleErr || deptErr)?.message || null,
            });
            return;
          }
        }

        // Try RPC with 3s timeout to avoid blocking
        const rpcTimeout = new Promise<{ roleRes: any; deptRes: any; timedOut: boolean }>((resolve) => 
          setTimeout(() => resolve({ roleRes: null, deptRes: null, timedOut: true }), 3000)
        );
        
        const rpcCall = Promise.all([
          supabase.rpc('get_user_role', { _user_id: user.id }),
          supabase.rpc('get_user_department', { _user_id: user.id })
        ]).then(([{ data: roleRes, error: roleErr }, { data: deptRes, error: deptErr }]) => ({
          roleRes, deptRes, roleErr, deptErr, timedOut: false
        }));

        const result = await Promise.race([rpcCall, rpcTimeout]);
        
        if (result.timedOut) {
          console.warn('[useUserRole] RPC timeout (3s), using fallback role=null', { traceId });
          setRoleData({
            role: null,
            department: null,
            loading: false,
            error: 'Database timeout',
          });
          lastFailAtRef.current = Date.now();
          return;
        }

        const { roleRes, deptRes, roleErr, deptErr } = result as any;
        console.log('[useUserRole] RPC results', { traceId, roleErr, deptErr, roleRes, deptRes });

        const rpcRole = (roleRes as string) || null;
        const rpcDept = (deptRes as string) || null;

        // Cache the result
        if (!isImpersonating && (rpcRole || rpcDept)) {
          localStorage.setItem(cacheKey, JSON.stringify({ role: rpcRole, department: rpcDept }));
        }

        setRoleData({
          role: rpcRole as any,
          department: rpcDept as any,
          loading: false,
          error: (roleErr || deptErr)?.message || null,
        });
        console.log('[useUserRole] Role set from RPC', { traceId, role: rpcRole, department: rpcDept });
      } catch (err) {
        console.error('[useUserRole] Error', { traceId, err });
        setRoleData({
          role: null,
          department: null,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to fetch user role',
        });
        lastFailAtRef.current = Date.now();
      } finally {
        const dt = Math.round(performance.now() - t0);
        console.log('[useUserRole] fetchUserRole:end', { traceId, durationMs: dt });
        fetchingRef.current = false;
      }
    };

    fetchUserRole();

    // Only subscribe if not impersonating (avoid unnecessary subscriptions)
    if (!isImpersonating && user?.id) {
      if (subscribedRef.current) {
        console.log('[useUserRole] Subscription already active, skipping', { traceId });
        return;
      }

      const channel = supabase
        .channel(`user_role_changes_${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_roles',
            filter: `user_id=eq.${user.id}`,
          },
          () => {
            console.log('[useUserRole] Role changed, refetching');
            localStorage.removeItem(`user_role_${user?.id}`);
            fetchUserRole();
          }
        )
        .subscribe();

      subscribedRef.current = true;
      console.log('[useUserRole] Subscribed to changes', { traceId, channel: `user_role_changes_${user.id}` });

      return () => {
        console.log('[useUserRole] Cleaning up subscription');
        subscribedRef.current = false;
        supabase.removeChannel(channel);
      };
    }
  }, [user?.id, isImpersonating, impersonationLoading]);

  const isAdmin = roleData.role === 'admin';
  const isProcurementManager = roleData.role === 'procurement_manager';
  const isProviderManager = roleData.role === 'provider_manager';
  const isDepartmentManager = roleData.role === 'department_manager';
  const isQAManager = roleData.role === 'qa_manager';
  const isMAManager = roleData.role === 'ma_manager';
  const isTechApps = roleData.role === 'tech_apps';
  const isTechInfrastructure = roleData.role === 'tech_infrastructure';
  const isCreativeManager = roleData.role === 'creative_manager';

  const canAccessLab = (labType: string): boolean => {
    if (isAdmin) return true;
    
    // Creative Manager does NOT have access to labs
    if (isCreativeManager) return false;
    
    if (isProcurementManager || isProviderManager || isQAManager || isMAManager || isTechApps || isTechInfrastructure) return false; // These managers don't access labs
    if (!roleData.department) return false;
    
    const normalizedLabType = labType.toLowerCase();
    const allowedLabs = LAB_MAPPING[roleData.department] || [];
    return allowedLabs.some(lab => lab.toLowerCase() === normalizedLabType);
  };

  const getAllowedLabs = (): string[] => {
    if (isAdmin) {
      return ['aso', 'product', 'usability', 'compliance', 'ad-monetization', 'user-acquisition'];
    }
    // Creative Manager does NOT have access to labs
    if (isCreativeManager) return [];
    
    if (isProcurementManager || isProviderManager || isQAManager || isMAManager || isTechApps || isTechInfrastructure) return []; // No lab access
    if (!roleData.department) return [];
    return LAB_MAPPING[roleData.department] || [];
  };

  const getAllowedPages = (): string[] => {
    if (isAdmin) {
      // Admin has access to all pages, explicitly list them for clarity
      return [
        'all',
        '/operational-summary',
        '/performance-summary',
        '/overview',
        '/timeline-dashboard',
        '/task-manager',
        '/negotiation-manager',
        '/app-database',
        '/creative-database',
        '/providers-database',
        '/user-activity-monitor',
        '/watchtower-integration',
        '/admin',
        // Labs
        '/aso',
        '/product',
        '/usability',
        '/compliance',
        '/ad-monetization',
        '/user-acquisition'
      ];
    }
    if (isProcurementManager) {
      return [
        '/task-manager',
        '/app-database',
        '/creative-database',
        '/providers-database'
      ];
    }
    if (isProviderManager) {
      return [
        '/task-manager'
      ];
    }
    if (isQAManager) {
      return [
        '/app-database',
        '/task-manager'
      ];
    }
    if (isMAManager) {
      return [
        '/app-database',
        '/task-manager',
        '/negotiation-manager'
      ];
    }
    if (isTechApps || isTechInfrastructure) {
      return [
        '/app-database',
        '/task-manager'
      ];
    }
    if (isCreativeManager) {
      return [
        '/app-database',
        '/task-manager'
      ];
    }
    
    // Department managers get their labs + operational pages + portfolio summaries
    if (isDepartmentManager && roleData.department) {
      const pages = [
        '/task-manager',
        '/app-database'
      ];
      
      // Add performance summary for all department managers
      pages.push('/performance-summary');
      
      // Add lab pages based on department
      if (roleData.department === 'aso') {
        pages.push('/aso');
      } else if (roleData.department === 'product') {
        pages.push('/product', '/usability', '/usability-performance-summary');
      } else if (roleData.department === 'compliance') {
        pages.push('/compliance');
      } else if (roleData.department === 'ad_monetization') {
        pages.push('/ad-monetization');
      } else if (roleData.department === 'user_acquisition') {
        pages.push('/user-acquisition');
      }
      
      return pages;
    }
    
    return [];
  };

  return {
    ...roleData,
    isAdmin,
    isProcurementManager,
    isProviderManager,
    isDepartmentManager,
    isQAManager,
    isMAManager,
    isTechApps,
    isTechInfrastructure,
    isCreativeManager,
    canAccessLab,
    canCreateApps: isAdmin,
    canDeleteApps: isAdmin,
    canInviteUsers: isAdmin,
    canManageRoles: isAdmin,
    canViewAllTasks: isAdmin || isProcurementManager || isProviderManager || isQAManager || isMAManager || isTechApps || isTechInfrastructure || isCreativeManager,
    allowedLabs: getAllowedLabs(),
    allowedPages: getAllowedPages()
  };
}
